import React from "react";
import {
  FaCheckCircle,
  FaClock,
  FaCreditCard,
  FaEye,
  FaShoppingBag,
  FaUndo,
} from "react-icons/fa";

import { formatEuro } from "../utils/paymentsDashboardUtils";

function KpiCard({ label, value, detail, icon, loading, tone = "base" }) {
  const tones = {
    base: "border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900",
    ok: "border-emerald-200 bg-emerald-50 dark:border-emerald-500/20 dark:bg-emerald-500/10",
    warn: "border-amber-200 bg-amber-50 dark:border-amber-500/20 dark:bg-amber-500/10",
    danger: "border-rose-200 bg-rose-50 dark:border-rose-500/20 dark:bg-rose-500/10",
    info: "border-indigo-200 bg-indigo-50 dark:border-indigo-500/20 dark:bg-indigo-500/10",
    violet:
      "border-violet-200 bg-violet-50 dark:border-violet-500/20 dark:bg-violet-500/10",
  };

  return (
    <article
      className={`rounded-[24px] border p-4 shadow-sm ${
        tones[tone] || tones.base
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
            {label}
          </p>

          {loading ? (
            <div className="mt-3 h-7 w-24 animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800" />
          ) : (
            <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">
              {value}
            </p>
          )}

          {detail ? (
            <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-slate-300">
              {detail}
            </p>
          ) : null}
        </div>

        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-indigo-700 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-indigo-300 dark:ring-slate-700">
          {icon}
        </div>
      </div>
    </article>
  );
}

export default function PaymentsKpiCards({
  stats,
  filteredStats,
  loading,
  selectedSeason,
}) {
  const seasonLabel = selectedSeason ? `Saison ${selectedSeason}` : "Toutes saisons";

  return (
    <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-6">
      <KpiCard
        label="Paiements"
        value={stats.total}
        detail={`${filteredStats.total} affiché(s) avec les filtres • ${seasonLabel}`}
        icon={<FaCreditCard />}
        loading={loading}
        tone="info"
      />

      <KpiCard
        label="Somme saison"
        value={formatEuro(stats.totalAmount)}
        detail={`${formatEuro(filteredStats.totalAmount)} avec les filtres`}
        icon={<FaShoppingBag />}
        loading={loading}
        tone="violet"
      />

      <KpiCard
        label="Réglés"
        value={formatEuro(stats.paidAmount)}
        detail={`${stats.paidCount} règlement(s) réglé(s) • ${seasonLabel}`}
        icon={<FaCheckCircle />}
        loading={loading}
        tone="ok"
      />

      <KpiCard
        label="À suivre"
        value={formatEuro(stats.pendingAmount)}
        detail={`${stats.pendingCount} en attente/non réglé(s) • ${stats.partialCount} partiel(s)`}
        icon={<FaClock />}
        loading={loading}
        tone={stats.pendingCount ? "warn" : "base"}
      />

      <KpiCard
        label="Affichés"
        value={stats.authorizedCount}
        detail={`${stats.hiddenCount} masqué(s) côté utilisateur`}
        icon={<FaEye />}
        loading={loading}
      />

      <KpiCard
        label="Remboursés"
        value={formatEuro(stats.refundedAmount)}
        detail={`${stats.refundedCount} remboursement(s)`}
        icon={<FaUndo />}
        loading={loading}
        tone={stats.refundedCount ? "danger" : "base"}
      />
    </section>
  );
}